import React, { useState, useEffect } from 'react';
import Masonry from 'react-masonry-css';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { storage, db, hasStorageBucket } from './firebase';
import { ref, listAll, getDownloadURL } from 'firebase/storage';
import { doc, getDoc } from 'firebase/firestore';
import LazyLoad from 'react-lazyload';
import CategoryMenu from './CategoryMenu';
import styles from './styles/PhotoGallery.module.css';

function PhotoGallery({ setIsLightboxOpen }) {
  const [images, setImages] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('Kompositionen');
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Überwacht die Fensterbreite
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // initial prüfen
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchImages = async () => {
      if (!hasStorageBucket || !storage) {
        console.warn('Firebase storageBucket fehlt. Bilder werden lokal nicht geladen.');
        setImages([]);
        return;
      }

      try {
        const imagesRef = ref(storage, 'images');
        const result = await listAll(imagesRef);

        if (!result.items || result.items.length === 0) {
          console.warn('Keine Bilder im angegebenen Pfad gefunden.');
          return;
        }

        const imagePromises = result.items.map(async (itemRef) => {
          try {
            if (!itemRef.name) {
              console.warn('itemRef hat keinen Namen:', itemRef);
              return null;
            }
            const url = await getDownloadURL(itemRef);
            const fileName = itemRef.name;
            const imageNumber = fileName.split('.')[0];
            const docRef = doc(db, 'descriptions', imageNumber);
            const docSnap = await getDoc(docRef);
            let description = '';
            let category = '';

            if (docSnap.exists()) {
              description = docSnap.data().description;
              category = docSnap.data().category || '';
            } else {
              description = 'Keine Beschreibung verfügbar';
            }

            return { url, description, category };
          } catch (error) {
            console.error('Fehler beim Verarbeiten des Bildes:', error);
            return null;
          }
        });

        const imageData = (await Promise.all(imagePromises)).filter((img) => img !== null);
        setImages(imageData);
      } catch (error) {
        console.error('Fehler beim Laden der Bilder:', error);
      }
    };

    fetchImages();
  }, []);

  useEffect(() => {
    setIsLightboxOpen(isOpen);
  }, [isOpen, setIsLightboxOpen]);

  const breakpointColumnsObj = {
    default: 3,
    1300: 2,
    700: 1,
  };

  // Filtere Bilder basierend auf der ausgewählten Kategorie
  const filteredImages = images.filter((image) => image.category === selectedCategory);

  return (
    <div className={styles.photoGalleryContainer}>
      <CategoryMenu
        categories={['Kompositionen', 'Improvisationen', 'Studien', 'Landschaften']}
        onCategorySelect={(category) => setSelectedCategory(category)}
        selectedCategory={selectedCategory}
      />

      <div className={styles.galleryContainer}>
        <Masonry
          breakpointCols={breakpointColumnsObj}
          className={styles.myMasonryGrid}
          columnClassName={styles.myMasonryGridColumn}
        >
          {filteredImages.map((image, index) => (
            <div
              key={image.url}
              className={styles.galleryItem}
              style={{ animationDelay: `${index * 60}ms` }}
            >
              <LazyLoad
                height={200}
                offset={100}
                placeholder={
                  <div style={{ height: '200px', backgroundColor: '#f0f0f0' }}>
                    Lädt...
                  </div>
                }
              >
                <img
                  src={image.url}
                  alt={image.description}
                  onClick={() => {
                    setPhotoIndex(index);
                    setIsOpen(true);
                  }}
                />
              </LazyLoad>
              <div className={styles.overlay}>
                <span className={styles.overlayCategory}>{image.category}</span>
                <span className={styles.overlayTitle}>{image.description}</span>
              </div>
            </div>
          ))}
        </Masonry>

        {isOpen && filteredImages.length > 0 && (
          <Lightbox
            open={isOpen}
            close={() => setIsOpen(false)}
            index={photoIndex}
            slides={filteredImages.map((image) => ({
              src: image.url,
              title: image.description,
            }))}
            styles={{
              container: {
                padding: isMobile ? 5 : 50,
                backgroundColor: 'rgba(15, 12, 9, 0.8)',
              },
            }}
            on={{
              view: ({ index }) => setPhotoIndex(index),
            }}
            render={{
              slide: ({ slide, rect }) => (
                <div className={styles.lightboxSlide}>
                  <img
                    src={slide.src}
                    alt={slide.title || 'Artwork'}
                    style={{
                      maxWidth: rect.width * 0.94,
                      maxHeight: rect.height * 0.9,
                      objectFit: 'contain',
                      borderRadius: 0,
                      boxShadow: '0 20px 50px rgba(0,0,0,0.45)',
                    }}
                  />
                  {slide.title && (
                    <div className={styles.lightboxFooter}>{slide.title}</div>
                  )}
                </div>
              ),
            }}
          />
        )}
      </div>
    </div>
  );
}

export default PhotoGallery;
